import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Key, Pencil, Plus, Shield, Clock, AlertTriangle } from "lucide-react";
import { useApiKeys, useRevokeApiKey, useRenameApiKey } from "@/features/api-keys/hooks/useApiKeys";
import { CreateApiKeyDialog } from "@/features/api-keys/components/CreateApiKeyDialog";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Dialog } from "@/components/ui/Dialog";
import { formatDate } from "@/lib/formatters";
import type { ApiKey } from "@/features/api-keys/api/api-keys.api";

export const Route = createFileRoute("/_authenticated/api-keys")({
  component: ApiKeysPage,
});

export function ApiKeysPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useApiKeys();
  const revokeApiKey = useRevokeApiKey();
  const renameApiKey = useRenameApiKey();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<ApiKey | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const keys = data ?? [];
  const activeKeys = keys.filter((k) => !k.revoked_at);
  const revokedKeys = keys.filter((k) => k.revoked_at);

  const closeRename = () => {
    if (renameApiKey.isPending) return;
    setRenameTarget(null);
    renameApiKey.reset();
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-heading">{t("apiKeys.title")}</h1>
          <p className="mt-1 text-sm text-muted">{t("apiKeys.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
        >
          <Plus size={16} />
          {t("apiKeys.createKey")}
        </button>
      </div>

      {isLoading && <PageLoader />}

      <ApiErrorBanner error={error} className="mt-8" />

      {!isLoading && !error && keys.length === 0 && (
        <div className="mt-16 flex flex-col items-center gap-6 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-indigo/10">
            <Key size={36} className="text-indigo" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-heading">{t("apiKeys.emptyTitle")}</h2>
            <p className="mt-1 max-w-sm text-sm text-muted">{t("apiKeys.emptySubtitle")}</p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
          >
            <Plus size={16} />
            {t("apiKeys.createKey")}
          </button>
        </div>
      )}

      {!isLoading && !error && activeKeys.length > 0 && (
        <div className="mt-6 space-y-3">
          {activeKeys.map((key) => (
            <div
              key={key.id}
              className="rounded-xl border border-border bg-surface p-5 transition-all hover:border-border-light"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo/10 to-cyan/10">
                    <Key size={18} className="text-indigo" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-heading">{key.name}</h3>
                    <p className="mt-0.5 font-mono text-xs text-muted">{key.key_prefix}…</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRenameTarget(key);
                      setRenameValue(key.name);
                    }}
                    title={t("apiKeys.rename")}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setRevokeKeyId(key.id)}
                    className="rounded-lg border border-danger/20 px-3 py-1.5 text-xs font-medium text-danger transition-colors hover:bg-danger/5"
                  >
                    {t("apiKeys.revoke")}
                  </button>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted">
                <span className="inline-flex items-center gap-1">
                  <Shield size={12} />
                  {key.scopes.join(", ")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} />
                  {t("apiKeys.createdAt")}:{" "}
                  {formatDate(key.created_at, undefined, t("apiKeys.never"))}
                </span>
                {key.expires_at && (
                  <span className="inline-flex items-center gap-1">
                    <AlertTriangle size={12} />
                    {t("apiKeys.expiresAt")}: {formatDate(key.expires_at)}
                  </span>
                )}
                <span>
                  {t("apiKeys.lastUsed")}:{" "}
                  {formatDate(key.last_used_at, undefined, t("apiKeys.never"))}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && revokedKeys.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-3 text-sm font-medium text-muted">{t("apiKeys.revoked")}</h3>
          <div className="space-y-2">
            {revokedKeys.map((key) => (
              <div
                key={key.id}
                className="rounded-lg border border-border bg-surface/50 p-4 opacity-60"
              >
                <div className="flex items-center gap-3">
                  <Key size={14} className="text-muted" />
                  <span className="text-sm text-muted line-through">{key.name}</span>
                  <span className="font-mono text-xs text-muted">{key.key_prefix}…</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CreateApiKeyDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <Dialog open={renameTarget !== null} onClose={closeRename} title={t("apiKeys.rename")}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (renameApiKey.isPending) return;
            const trimmed = renameValue.trim();
            if (!renameTarget || !trimmed || trimmed === renameTarget.name) return;
            renameApiKey.mutate(
              { id: renameTarget.id, name: trimmed },
              { onSuccess: () => setRenameTarget(null) },
            );
          }}
        >
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            aria-label={t("apiKeys.name")}
            data-autofocus
            readOnly={renameApiKey.isPending}
            aria-disabled={renameApiKey.isPending || undefined}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
          {renameApiKey.error && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {renameApiKey.error.error ?? t("common.error")}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeRename}
              aria-disabled={renameApiKey.isPending || undefined}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={
                !renameApiKey.isPending &&
                (!renameValue.trim() || !renameTarget || renameValue.trim() === renameTarget.name)
              }
              aria-disabled={renameApiKey.isPending || undefined}
              aria-busy={renameApiKey.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {renameApiKey.isPending ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={revokeKeyId !== null}
        onClose={() => setRevokeKeyId(null)}
        onConfirm={() => {
          if (revokeKeyId) revokeApiKey.mutate(revokeKeyId);
        }}
        title={t("apiKeys.revoke")}
        description={t("apiKeys.revokeConfirm")}
        confirmLabel={t("apiKeys.revoke")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={revokeApiKey.isPending}
      />
    </div>
  );
}
